import { DeesElement, customElement, html, css, cssManager, state } from '@design.estate/dees-element';
import type { IStorageDataProvider, IStorageObject } from '../elements/00group-dataview/dees-storage-browser/interfaces.js';
import { themeDefaultStyles } from '../elements/00theme.js';
import '../elements/00group-button/dees-button/dees-button.js';
// Disposable in-memory fixtures. No external service is used.
export class DemoStorageDataProvider implements IStorageDataProvider {
  private objects: Map<string, { content: string; contentType: string; size: number; lastModified: string }> = new Map();

  constructor(initialFiles?: Array<{ key: string; content: string; contentType: string }>, private onMutation?: (message: string) => void) {
    const now = new Date().toISOString();
    if (initialFiles) {
      for (const file of initialFiles) this.objects.set(file.key, { content: btoa(file.content), contentType: file.contentType, size: file.content.length, lastModified: now });
      return;
    }
    // Seed with sample data
    this.objects.set('documents/readme.md', {
      content: btoa('# Welcome\n\nThis is a demo Storage browser.\n'),
      contentType: 'text/markdown',
      size: 42,
      lastModified: now,
    });
    this.objects.set('documents/config.json', {
      content: btoa('{\n  "name": "demo",\n  "version": "1.0.0"\n}'),
      contentType: 'application/json',
      size: 48,
      lastModified: now,
    });
    this.objects.set('documents/notes/todo.txt', {
      content: btoa('Buy milk\nFix bug #42\nDeploy to production'),
      contentType: 'text/plain',
      size: 45,
      lastModified: now,
    });
    const artwork = '<svg xmlns="http://www.w3.org/2000/svg" width="720" height="440" viewBox="0 0 720 440"><rect width="720" height="440" rx="32" fill="#101828"/><circle cx="570" cy="88" r="160" fill="#007aff"/><path d="M0 330 Q160 150 350 310 T720 290 V440 H0" fill="#64d2ff"/><text x="48" y="96" font-family="sans-serif" font-size="32" fill="white">Gateway workspace</text></svg>';
    this.objects.set('images/workspace.svg', { content: btoa(artwork), contentType: 'image/svg+xml', size: artwork.length, lastModified: now });
    this.objects.set('scripts/deploy.sh', {
      content: btoa('#!/bin/bash\necho "Deploying..."\n'),
      contentType: 'text/plain',
      size: 34,
      lastModified: now,
    });
    this.objects.set('index.html', {
      content: btoa('<!DOCTYPE html>\n<html>\n<body>\n  <h1>Hello World</h1>\n</body>\n</html>'),
      contentType: 'text/html',
      size: 72,
      lastModified: now,
    });
    this.objects.set('styles.css', {
      content: btoa('body { margin: 0; font-family: sans-serif; }'),
      contentType: 'text/css',
      size: 44,
      lastModified: now,
    });
  }

  async listObjects(bucket: string, prefix?: string, delimiter?: string): Promise<{ objects: IStorageObject[]; prefixes: string[] }> {
    const pfx = prefix || '';
    const objects: IStorageObject[] = [];
    const prefixes = new Set<string>();

    for (const [key, data] of this.objects) {
      if (!key.startsWith(pfx)) continue;
      const rest = key.slice(pfx.length);

      if (delimiter) {
        const slashIndex = rest.indexOf(delimiter);
        if (slashIndex >= 0) {
          prefixes.add(pfx + rest.slice(0, slashIndex + 1));
        } else {
          objects.push({ key, size: data.size, lastModified: data.lastModified });
        }
      } else {
        objects.push({ key, size: data.size, lastModified: data.lastModified });
      }
    }

    return { objects, prefixes: Array.from(prefixes).sort() };
  }

  async getObject(bucket: string, key: string): Promise<{ content: string; contentType: string; size: number; lastModified: string }> {
    const obj = this.objects.get(key);
    if (!obj) throw new Error('Not found');
    return { ...obj };
  }

  async putObject(bucket: string, key: string, base64Content: string, contentType: string): Promise<boolean> {
    this.objects.set(key, {
      content: base64Content,
      contentType,
      size: atob(base64Content).length,
      lastModified: new Date().toISOString(),
    });
    this.onMutation?.(`Saved ${key}`);
    return true;
  }

  async deleteObject(bucket: string, key: string): Promise<boolean> {
    const deleted = this.objects.delete(key);
    if (deleted) this.onMutation?.(`Deleted ${key}`);
    return deleted;
  }

  async deletePrefix(bucket: string, prefix: string): Promise<boolean> {
    for (const key of this.objects.keys()) {
      if (key.startsWith(prefix)) {
        this.objects.delete(key);
      }
    }
    this.onMutation?.(`Deleted folder ${prefix}`);
    return true;
  }

  async getObjectUrl(bucket: string, key: string): Promise<string> {
    const obj = this.objects.get(key);
    if (!obj) return '';
    return `data:${obj.contentType};base64,${obj.content}`;
  }

  async moveObject(bucket: string, sourceKey: string, destKey: string): Promise<{ success: boolean; error?: string }> {
    const obj = this.objects.get(sourceKey);
    if (!obj) return { success: false, error: 'Source not found' };
    if (sourceKey === destKey) return { success: true };
    if (this.objects.has(destKey)) return { success: false, error: 'Destination already exists' };
    this.objects.set(destKey, { ...obj, lastModified: new Date().toISOString() });
    this.objects.delete(sourceKey);
    this.onMutation?.(`Moved ${sourceKey} to ${destKey}`);
    return { success: true };
  }

  async movePrefix(bucket: string, sourcePrefix: string, destPrefix: string): Promise<{ success: boolean; movedCount?: number; error?: string }> {
    if (destPrefix.startsWith(sourcePrefix)) return { success: false, error: 'Choose a destination outside this folder' };
    let count = 0;
    const toMove = Array.from(this.objects.entries()).filter(([k]) => k.startsWith(sourcePrefix));
    if (toMove.some(([key]) => this.objects.has(destPrefix + key.slice(sourcePrefix.length)))) return { success: false, error: 'Destination already exists' };
    for (const [key, data] of toMove) {
      const newKey = destPrefix + key.slice(sourcePrefix.length);
      this.objects.set(newKey, { ...data, lastModified: new Date().toISOString() });
      this.objects.delete(key);
      count++;
    }
    this.onMutation?.(`Moved folder ${sourcePrefix} to ${destPrefix}`);
    return { success: true, movedCount: count };
  }
}


@customElement('dees-demo-storage-workspace')
export class DeesDemoStorageWorkspace extends DeesElement {
  @state() private accessor provider = new DemoStorageDataProvider();
  public static styles = [themeDefaultStyles, cssManager.defaultStyles, css`
    :host { display: flex; flex-direction: column; gap: 16px; min-width: 0; font-family: var(--dees-font-family); }
    .toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
    p { margin: 0; font-size: 12px; line-height: 1.5; color: var(--dees-color-text-secondary); }
    dees-storage-browser { height: 560px; min-height: 360px; }
    @media (max-width: 600px) { dees-storage-browser { height: 540px; } }
  `];
  public canDeactivate() { return this.shadowRoot?.querySelector('dees-storage-browser')?.canLeave() ?? true; }
  public async firstUpdated() {
    const browser = this.shadowRoot?.querySelector('dees-storage-browser');
    await browser?.updateComplete;
    browser?.setLocation({ prefix: 'images/', key: 'images/workspace.svg' });
  }
  public render() {
    return html`<div class="toolbar"><p>Workspace files · changes stay in this demo</p><dees-button type="ghost" text="Reset files" @clicked=${() => {
      const browser = this.shadowRoot?.querySelector('dees-storage-browser');
      if (!browser || browser.canLeave()) this.provider = new DemoStorageDataProvider();
    }}></dees-button></div>
      <dees-storage-browser .dataProvider=${this.provider} bucketName="workspace"></dees-storage-browser>
      <p>Open a folder or file. Use the + menu to create or upload, and the row actions to rename, move, or delete.</p>`;
  }
}
